import { useState } from 'react';
import {
  Controller,
  ControllerRenderProps,
  UseFormReturn,
} from 'react-hook-form';

import { AnimatePresence, motion } from 'framer-motion';
import {
  BookOpenIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  TrashIcon,
} from 'lucide-react';

import { ActionButton } from 'components/ActionButton';
import { Badge } from 'components/Badge';
import { DatePicker } from 'components/DatePicker';
import { Label } from 'components/Label';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/Tooltip';
import { Input } from 'components/input';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import {
  LTLAccessorial,
  LTLSelectedAccessorial,
  QuickQuoteInputs,
} from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import { cn } from 'utils/shadcn';

import { formatFieldLabel } from './accessorialUtils';

const ANIMATIONS = {
  card: {
    transition: { type: 'spring' as const, duration: 0.22, bounce: 0.15 },
    initial: {
      opacity: 0,
      scale: 0.98,
      height: 0,
      marginTop: 0,
      marginBottom: 0,
    },
    animate: {
      opacity: 1,
      scale: 1,
      height: 'auto',
      marginTop: 8,
      marginBottom: 8,
    },
    exit: { opacity: 0, scale: 0.98, height: 0, marginTop: 0, marginBottom: 0 },
  },
  body: {
    initial: { height: 0, opacity: 0 },
    animate: { height: 'auto', opacity: 1 },
    exit: { height: 0, opacity: 0 },
    transition: { duration: 0.15 },
  },
};

interface AccessorialCardProps {
  index: number;
  selected: LTLSelectedAccessorial;
  def?: LTLAccessorial;
  categoryConfig?: { label: string; color: string };
  control: UseFormReturn<QuickQuoteInputs, any, QuickQuoteInputs>['control'];
  onRemove: () => void;
}

export function AccessorialCard({
  index,
  selected,
  def,
  categoryConfig,
  control,
  onRemove,
}: AccessorialCardProps) {
  const [open, setOpen] = useState(true);
  const hasActions = def?.actions && Object.keys(def.actions).length > 0;

  return (
    <motion.div
      {...ANIMATIONS.card}
      layout
      className='rounded-sm border border-neutral-300 relative w-full mb-0!'
    >
      {categoryConfig && (
        <span
          aria-hidden='true'
          title={categoryConfig.label}
          className={cn(
            'absolute left-0 top-0 h-full w-1.5 rounded-l-sm',
            categoryConfig.color
          )}
        />
      )}

      <Flex
        align='center'
        justify='between'
        gap='xs'
        className='px-2 py-1.5 w-full min-w-0'
      >
        <Typography
          variant='body-sm'
          weight='medium'
          className='min-w-0 flex-1 truncate text-neutral-800 ml-2'
          title={selected.name}
        >
          {selected.name}
        </Typography>

        <Flex align='center' gap='xs' className='shrink-0'>
          {selected.recommendedByShiplify && (
            <Badge
              variant='secondary'
              className='shrink-0 rounded-full border border-brand-300 bg-brand-50 px-1.5 py-0.5'
              title='Recommended by Shiplify'
            >
              <img
                src='https://lp.shiplify.com/hs-fs/hubfs/Shiplify_Logo_Primary_RGB.png?width=120&height=24&name=Shiplify_Logo_Primary_RGB.png'
                alt='Recommended by Shiplify'
                className='h-2.5 w-auto'
              />
            </Badge>
          )}
          {selected.fromAddressBookStop && (
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <span
                  aria-label={`From Tai address book · ${selected.fromAddressBookStop === 'pickup' ? 'Pickup' : 'Dropoff'}`}
                  className='inline-flex h-6 w-6 shrink-0 cursor-help items-center justify-center rounded-sm text-neutral-500 hover:bg-neutral-100 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-neutral-500'
                  role='img'
                  tabIndex={0}
                >
                  <BookOpenIcon aria-hidden='true' className='h-4 w-4' />
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {`From Tai address book · ${selected.fromAddressBookStop === 'pickup' ? 'Pickup' : 'Dropoff'}`}
              </TooltipContent>
            </Tooltip>
          )}
          {hasActions && (
            <button
              type='button'
              onClick={() => setOpen((o) => !o)}
              aria-label={open ? 'Collapse' : 'Expand'}
              className='inline-flex items-center justify-center rounded-sm p-1 w-6 h-6 shrink-0 cursor-pointer text-neutral-500 hover:bg-neutral-100'
              tabIndex={0}
            >
              {open ? (
                <ChevronUpIcon className='h-4 w-4' />
              ) : (
                <ChevronDownIcon className='h-4 w-4' />
              )}
            </button>
          )}

          <ActionButton
            onClick={onRemove}
            className='inline-flex items-center gap-1 hover:bg-error-300 text-error-600'
            icon={TrashIcon}
            aria-label='Remove accessorial'
          />
        </Flex>
      </Flex>

      <AnimatePresence initial={false}>
        {open && hasActions && (
          <motion.div {...ANIMATIONS.body} className='ml-4 mr-3 my-3'>
            <Flex direction='col' gap='md' className='w-full mx-0'>
              {Object.entries(def!.actions!).map(([fieldName, fieldType]) => (
                <div key={fieldName} className='min-w-0'>
                  <Label
                    name={`ltl.selectedAccessorials.${index}.actionValues.${fieldName}`}
                    required={false}
                    className='whitespace-normal wrap-break-word text-[13px]'
                  >
                    {formatFieldLabel(fieldName)}
                  </Label>
                  <Controller
                    name={
                      `ltl.selectedAccessorials.${index}.actionValues.${fieldName}` as any
                    }
                    control={control}
                    render={({
                      field,
                    }: {
                      field: ControllerRenderProps<QuickQuoteInputs, any>;
                    }) =>
                      fieldType === 'date' ? (
                        <div className='mt-1'>
                          <DatePicker field={field} />
                        </div>
                      ) : (
                        <Input
                          {...field}
                          placeholder={`Enter ${formatFieldLabel(fieldName).toLowerCase()}`}
                          className='mt-1 w-full'
                        />
                      )
                    }
                  />
                </div>
              ))}
            </Flex>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
